/**
 * Catálogo canônico dos Gatilhos de Proteção do modo H_c — ADR-148
 * (materialização do Eixo I da ADR-030).
 *
 * Fonte da verdade triangulada (R15):
 * - **Semântica dos gatilhos**: ADR-030 Eixo I (7 duros + 4 viradas como
 *   condição da autonomia delegada — P5 "IA conduz COM proteção") + 2 defesas
 *   de runtime já-vivas (`loop`/`tool-failure`, computeLoopSignal/
 *   computeToolFailureCount no AIConductorService) incorporadas para o
 *   teste-guard fechar 1:1 com o enum runtime.
 * - **IDs**: casam com `TRIGGER_REASONS` do `trigger-output.dto.ts` onde o
 *   enum existe (rename no caminho quente seria arriscado). Um teste-guard no
 *   Engine (`protection-trigger-catalog-consistency.spec.ts`) trava a
 *   divergência catálogo×runtime (molde v3.241).
 * - **Piso de plataforma**: essência ética da ADR-030 ("Nível 4 sempre escala;
 *   cliente pedindo humano sempre é atendido; ações irreversíveis sempre
 *   exigem humano") + defesas de runtime. Backend recusa desligar — não é só
 *   o frontend que esconde.
 * - **Dupla soberania** (ADR-148): piso de plataforma > mínimos do tenant
 *   (`Tenant.protectionTriggerMinimums`, Admin) > config por playbook
 *   (`Playbook.triggers`, Gestor) > default deste catálogo. Resolução efetiva
 *   única em `resolveEffectiveTriggers()` (./resolve.ts) — BE runtime e FE
 *   (estados travados) consomem a MESMA função.
 *
 * Nota de honestidade: gatilhos `signal='pending'` são configuráveis (intenção
 * de política) porém INERTES — não há detector canônico no runtime. A UI exibe
 * selo honesto; construir o detector é decisão futura explícita (ADR-148).
 * Proteção fingida é pior que pendência declarada.
 *
 * Mudança de semântica (gatilho novo, piso, default) = via humano + incremento
 * de `PROTECTION_TRIGGER_CATALOG_VERSION` (molde régua v3.238 / field-map LGPD).
 */
export const PROTECTION_TRIGGER_CATALOG_VERSION = 1;
/** Default histórico do worker de timeout (env `LUME_CONDUCTOR_TIMEOUT_INACTIVITY_MS` pode sobrescrever server-side). */
export const DEFAULT_DELEGATION_TIMEOUT_MINUTES = 30;
export const DEFAULT_MAX_CONSECUTIVE_REJECTIONS = 3;
export const PROTECTION_TRIGGER_CATALOG = [
    // ── Gatilhos de escalação dura (handoff H_c → H_a) ──────────────────────
    {
        id: 'level-4',
        kind: 'dura',
        label: 'Sensibilidade Nível 4 (Crítica)',
        description: 'Conversa classificada Nível 4 (risco de vida, ideação suicida, ameaça jurídica grave) — a IA devolve imediatamente ao humano. Além do trigger-agent, há check determinístico no conductor (não depende do LLM honrar).',
        signal: 'live',
        platformFloor: true,
        defaultEnabled: true,
    },
    {
        id: 'customer-requested-human',
        kind: 'dura',
        label: 'Cliente pediu humano',
        description: 'O cliente pediu explicitamente para falar com uma pessoa — sempre é atendido (compromisso ético da plataforma).',
        signal: 'live',
        platformFloor: true,
        defaultEnabled: true,
    },
    {
        id: 'irreversible-action',
        kind: 'dura',
        label: 'Ação irreversível solicitada',
        description: 'Ação externa crítica solicitada (abrir OS, alterar contrato, cancelar) — exige decisão humana.',
        signal: 'live',
        platformFloor: true,
        defaultEnabled: true,
    },
    {
        id: 'loop',
        kind: 'dura',
        label: 'IA em loop',
        description: 'Respostas repetidas em turnos consecutivos — defesa automática da plataforma, devolve ao humano.',
        signal: 'live',
        platformFloor: true,
        defaultEnabled: true,
    },
    {
        id: 'tool-failure',
        kind: 'dura',
        label: 'Falhas de ferramenta',
        description: 'Falhas consecutivas de ferramenta externa — defesa automática da plataforma, devolve ao humano.',
        signal: 'live',
        platformFloor: true,
        defaultEnabled: true,
    },
    {
        id: 'delegation-timeout',
        kind: 'dura',
        label: 'Timeout de delegação',
        description: 'H_c sem turno produtivo por X minutos — devolve ao humano. Threshold configurável por playbook (default 30min = comportamento atual).',
        signal: 'live',
        platformFloor: false,
        defaultEnabled: true,
        params: [
            {
                key: 'timeoutMinutes',
                label: 'Minutos sem progresso até devolver',
                min: 1,
                max: 1440,
                default: DEFAULT_DELEGATION_TIMEOUT_MINUTES,
            },
        ],
    },
    {
        id: 'attack-spam',
        kind: 'dura',
        label: 'Ataque / spam',
        description: 'Ataque, spam ou flood detectado — devolve ao humano. SEM detector canônico no runtime ainda (configurável como intenção de política; inerte até o detector existir).',
        signal: 'pending',
        platformFloor: false,
        defaultEnabled: false,
    },
    {
        id: 'validation-rejected',
        kind: 'dura',
        label: 'Validação reprovada em sequência',
        description: 'Validação automática reprovou N vezes consecutivas — devolve ao humano. SEM contador consecutivo no runtime ainda (inerte até o detector existir).',
        signal: 'pending',
        platformFloor: false,
        defaultEnabled: false,
        params: [
            {
                key: 'maxConsecutiveRejections',
                label: 'Reprovações consecutivas até devolver',
                min: 1,
                max: 10,
                default: DEFAULT_MAX_CONSECUTIVE_REJECTIONS,
            },
        ],
    },
    {
        id: 'financial-commitment',
        kind: 'dura',
        label: 'Compromisso financeiro',
        description: 'Mensagem do cliente envolve compromisso/promessa/decisão financeira (cancelar, multa, prazo, valor) — devolve ao humano. SEM detector IA em tempo real ainda (inerte até o detector existir).',
        signal: 'pending',
        platformFloor: false,
        defaultEnabled: false,
    },
    // ── Gatilhos de virada de playbook (gate grosso do transition-agent) ────
    {
        id: 'sentiment-deteriorating',
        kind: 'virada',
        label: 'Sentimento em deterioração',
        description: 'Sentimento do cliente em queda — vira playbook (H_c) ou propõe virada (H_a/H_b, conforme a matriz de transição).',
        signal: 'live',
        platformFloor: false,
        defaultEnabled: true,
    },
    {
        id: 'scope-shift',
        kind: 'virada',
        label: 'Mudança de escopo',
        description: 'O tópico saiu do escopo da persona/playbook atual — avalia virada.',
        signal: 'live',
        platformFloor: false,
        defaultEnabled: true,
    },
    {
        id: 'commercial-signals',
        kind: 'virada',
        label: 'Sinais comerciais',
        description: 'Cliente menciona termos comerciais (cancelar, contratar, plano) — avalia virada para playbook comercial/retenção.',
        signal: 'live',
        platformFloor: false,
        defaultEnabled: true,
    },
    {
        id: 'sensitive-content',
        kind: 'virada',
        label: 'Conteúdo sensível',
        description: 'Cliente menciona termos sensíveis (saúde, jurídico, financeiro detalhado — classificação Nível 3-4) — avalia virada.',
        signal: 'live',
        platformFloor: false,
        defaultEnabled: true,
    },
];
export const PROTECTION_TRIGGER_IDS = PROTECTION_TRIGGER_CATALOG.map((t) => t.id);
/** Os 5 gatilhos sempre-on não-removíveis (essência ética ADR-030 + defesas de runtime). */
export const PLATFORM_FLOOR_TRIGGER_IDS = PROTECTION_TRIGGER_CATALOG.filter((t) => t.platformFloor).map((t) => t.id);
/** Os 8 gatilhos configuráveis (Admin pode marcar como mínimo do tenant; Gestor configura por playbook). */
export const CONFIGURABLE_TRIGGER_IDS = PROTECTION_TRIGGER_CATALOG.filter((t) => !t.platformFloor).map((t) => t.id);
export const HARD_TRIGGER_IDS = PROTECTION_TRIGGER_CATALOG.filter((t) => t.kind === 'dura').map((t) => t.id);
export const TRANSITION_TRIGGER_IDS = PROTECTION_TRIGGER_CATALOG.filter((t) => t.kind === 'virada').map((t) => t.id);
const CATALOG_BY_ID = new Map(PROTECTION_TRIGGER_CATALOG.map((t) => [t.id, t]));
export function getProtectionTrigger(id) {
    return CATALOG_BY_ID.get(id) ?? null;
}
export function isProtectionTriggerId(id) {
    return CATALOG_BY_ID.has(id);
}
const _assertThirteen = true;
void _assertThirteen;
/** Os 5 primeiros são o piso de plataforma; o 6º (delegation-timeout) não é. */
const _assertFloor0 = true;
const _assertFloor4 = true;
const _assertNotFloor5 = false;
void _assertFloor0;
void _assertFloor4;
void _assertNotFloor5;
const _assertFloorLive = true;
const _assertFloorOn = true;
void _assertFloorLive;
void _assertFloorOn;
const _assertPendingOff = true;
void _assertPendingOff;
const _assertViradaNotFloor = true;
void _assertViradaNotFloor;
//# sourceMappingURL=catalog.js.map